import { Component, Input, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'frmg-gradient-selector',
  standalone: true,
  templateUrl: './gradient-selector.component.html',
  styleUrls: ['./gradient-selector.component.scss'],
  imports: [
    NgClass,
    MatTooltip
  ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GradientSelectorComponent),
    multi: true
  }]
})
export class GradientSelectorComponent implements ControlValueAccessor {
  @Input() set data(map: Record<string, string>) {
    this._data = map ?? {};
    this.items = Object.entries(this._data).map(([id, color]) => ({ id, color }));
    if (!this._receivedInitialValue) {
      this._selected = new Set(this.items.map(i => i.id));
      this.emit();
    } else {
      this._selected.forEach(id => { if (!this._data[id]) this._selected.delete(id); });
      this.items.forEach(i => { if (!this._selected.has(i.id)) {/* keep as-is */} });
      this.emit(false);
    }
  }
  @Input() value_descriptions: Record<string, string> = {};
  get data(): Record<string, string> { return this._data; }

  @Input() requireAtLeastOne = false;

  items: Array<{ id: string; color: string }> = [];
  disabled = false;

  private _data: Record<string, string> = {};
  private _selected = new Set<string>();
  private _onChange: (v: string[]) => void = () => {};
  private _onTouched: () => void = () => {};
  private _receivedInitialValue = false;

  writeValue(value: string[] | null): void {
    this._receivedInitialValue = true;
    if (!value || value.length === 0) {
      this._selected = new Set(this.items.map(i => i.id));
    } else {
      this._selected = new Set(value.filter(id => this._data[id]));
    }
  }
  registerOnChange(fn: any): void { this._onChange = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
  isSelected(id: string): boolean { return this._selected.has(id); }

  toggle(id: string): void {
    if (this.disabled) return;

    const wasSelected = this._selected.has(id);

    if (wasSelected) {
      if (this.requireAtLeastOne && this._selected.size === 1) {
        this.touch();
        return;
      }
      this._selected.delete(id);
    } else {
      this._selected.add(id);
    }

    this.emit();
  }

  onKeydown(e: KeyboardEvent, id: string) {
    if (this.disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.toggle(id);
    }
  }

  private emit(callChange = true) {
    const value = Array.from(this._selected);
    if (callChange) this._onChange(value);
  }

  private touch() { this._onTouched(); }

  @HostListener('blur')
  onBlur() { this.touch(); }
}
